// Mock API implementation for development
// This simulates a backend API for testing the authentication flow

// Mock database
const mockUsers = [
  {
    id: 1,
    email: 'test@example.com',
    password: 'password123',
    firstName: 'John',
    lastName: 'Doe',
    role: 'member',
    createdAt: '2024-01-01'
  },
  {
    id: 2,
    email: 'admin@mzansisave.co.za',
    password: 'admin123',
    firstName: 'Admin',
    lastName: 'User',
    role: 'admin',
    createdAt: '2024-01-01'
  },
  {
    id: 3,
    email: 'member@mzansisave.co.za',
    password: 'member123',
    firstName: 'Member',
    lastName: 'User',
    role: 'member',
    createdAt: '2024-01-01'
  }
];

// Generate mock JWT token
function generateMockToken(user) {
  const payload = {
    userId: user.id,
    email: user.email,
    role: user.role,
    iat: Date.now(),
    exp: Date.now() + (60 * 60 * 1000) // 1 hour expiry
  };
  
  return btoa(JSON.stringify(payload));
}

// Mock API responses
export const mockAuthAPI = {
  // Login endpoint
  login: async (email, password) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const user = mockUsers.find(u => u.email === email && u.password === password);
    
    if (user) {
      const token = generateMockToken(user);
      const { password: _, ...userWithoutPassword } = user;
      
      return {
        ok: true,
        json: async () => ({
          success: true,
          token,
          user: userWithoutPassword,
          message: 'Login successful'
        })
      };
    } else {
      return {
        ok: false,
        json: async () => ({
          success: false,
          message: 'Invalid email or password'
        })
      };
    }
  },

  // Register endpoint
  register: async (userData) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const { email, password, firstName, lastName } = userData;
    
    // Check if user exists
    const existingUser = mockUsers.find(u => u.email === email);
    if (existingUser) {
      return {
        ok: false,
        json: async () => ({
          success: false,
          message: 'User with this email already exists'
        })
      };
    }
    
    // Create new user
    const newUser = {
      id: mockUsers.length + 1,
      email,
      password,
      firstName,
      lastName,
      role: 'member',
      createdAt: new Date().toISOString()
    };
    
    mockUsers.push(newUser);
    
    const token = generateMockToken(newUser);
    const { password: _, ...userWithoutPassword } = newUser;
    
    return {
      ok: true,
      json: async () => ({
        success: true,
        token,
        user: userWithoutPassword,
        message: 'Registration successful'
      })
    };
  },

  // Profile endpoint
  profile: async (token) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const payload = JSON.parse(atob(token.replace('Bearer ', '')));
      
      if (payload.exp < Date.now()) {
        return {
          ok: false,
          status: 401,
          json: async () => ({
            success: false,
            message: 'Token expired'
          })
        };
      }
      
      const user = mockUsers.find(u => u.id === payload.userId);
      if (user) {
        const { password: _, ...userWithoutPassword } = user;
        return {
          ok: true,
          json: async () => ({
            success: true,
            user: userWithoutPassword
          })
        };
      }
    } catch (error) {
      // Invalid token
    }
    
    return {
      ok: false,
      status: 401,
      json: async () => ({
        success: false,
        message: 'Invalid token'
      })
    };
  }
};

// Intercept fetch requests for mock API
export function setupMockAPI() {
  const originalFetch = window.fetch;
  
  window.fetch = async (url, options = {}) => {
    // Check if it's an API call we should mock
    if (typeof url === 'string' && url.includes('/api/auth')) {
      
      if (url.includes('/api/auth/login') && options.method === 'POST') {
        const body = JSON.parse(options.body);
        return mockAuthAPI.login(body.email, body.password);
      }
      
      if (url.includes('/api/auth/register') && options.method === 'POST') {
        const body = JSON.parse(options.body);
        return mockAuthAPI.register(body);
      }
      
      if (url.includes('/api/auth/profile') && options.method === 'GET') {
        const token = options.headers?.Authorization;
        return mockAuthAPI.profile(token);
      }
    }
    
    // For all other requests, use the original fetch
    return originalFetch(url, options);
  };
}

// Setup mock API when in development
// Disabled to use real Supabase authentication
// if (process.env.NODE_ENV === 'development') {
//   setupMockAPI();
// }

export default mockAuthAPI;